import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import coaService from "../../services/coaService";
import { FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa";

const COAList = () => {
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const data = await coaService.getAccounts();
      setAccounts(data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this account?")) {
      try {
        await coaService.deleteAccount(id);
        fetchAccounts();
      } catch (error) {
        console.error("Error deleting account:", error);
      }
    }
  };

  const filteredAccounts = accounts.filter(
    (acc) =>
      acc.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.account_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.account_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getFullPathName = (account) => {
    if (!account.parent_id) return account.account_name;
    const parent = accounts.find((a) => a.id === account.parent_id);
    if (!parent) return account.account_name;
    if (!parent.parent_id) return `${parent.account_name} > ${account.account_name}`;
    const grandParent = accounts.find((a) => a.id === parent.parent_id);
    if (!grandParent) return `${parent.account_name} > ${account.account_name}`;
    return `${grandParent.account_name} > ${parent.account_name} > ${account.account_name}`;
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c" }}>
          Chart of Accounts
        </h2>
        <Link
          to="/dashboard/coa/new"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "linear-gradient(135deg, #00aaff 0%, #0077cc 100%)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "10px",
            textDecoration: "none",
            fontWeight: "600",
            boxShadow: "0 4px 14px rgba(0, 119, 204, 0.3)",
            transition: "transform 0.2s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
          onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        >
          <FaPlus /> Add Account
        </Link>
      </div>

      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          style={{
            position: "relative",
            marginBottom: "20px",
            maxWidth: "400px",
          }}
        >
          <FaSearch
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#a0aec0",
            }}
          />
          <input
            type="text"
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px 10px 40px",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#00aaff")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          />
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #f7fafc" }}>
                <th style={tableHeaderStyle}>Code</th>
                <th style={tableHeaderStyle}>Name</th>
                <th style={tableHeaderStyle}>Parent</th>
                <th style={tableHeaderStyle}>Type</th>
                <th style={tableHeaderStyle}>Opening Balance</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "40px", color: "#718096" }}>
                    Loading accounts...
                  </td>
                </tr>
              ) : filteredAccounts.length > 0 ? (
                filteredAccounts.map((account) => (
                  <tr
                    key={account.id}
                    style={{
                      borderBottom: "1px solid #f7fafc",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f8fbff")}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <td style={tableCellStyle}>{account.account_code}</td>
                    <td style={{ ...tableCellStyle, fontWeight: "600", color: "#2d3748" }}>
                      {getFullPathName(account)}
                    </td>
                    <td style={tableCellStyle}>
                      {account.parent_id 
                        ? accounts.find(a => a.id === account.parent_id)?.account_name || 'Unknown' 
                        : '-'}
                    </td>
                    <td style={tableCellStyle}>
                      <span style={getTypeBadgeStyle(account.account_type)}>
                        {account.account_type}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      {new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(account.opening_balance)}
                    </td>
                    <td style={tableCellStyle}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          background: account.is_active ? "#e6fffa" : "#fff5f5",
                          color: account.is_active ? "#2d3748" : "#c53030",
                        }}
                      >
                        {account.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Link
                          to={`/dashboard/coa/edit/${account.id}`}
                          style={{ color: "#4a5568", transition: "color 0.2s" }}
                          onMouseOver={(e) => (e.currentTarget.style.color = "#00aaff")}
                          onMouseOut={(e) => (e.currentTarget.style.color = "#4a5568")}
                        >
                          <FaEdit />
                        </Link>
                        <button
                          onClick={() => handleDelete(account.id)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#4a5568",
                            transition: "color 0.2s",
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.color = "#f56565")}
                          onMouseOut={(e) => (e.currentTarget.style.color = "#4a5568")}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "40px", color: "#a0aec0" }}>
                    No accounts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const tableHeaderStyle = {
  textAlign: "left",
  padding: "16px",
  color: "#718096",
  fontWeight: "600",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const tableCellStyle = {
  padding: "16px",
  color: "#4a5568",
  fontSize: "15px",
};

const getTypeBadgeStyle = (type) => {
  const styles = {
    Asset: { background: "#ebf8ff", color: "#2b6cb0" },
    Liability: { background: "#fffaf0", color: "#9c4221" },
    Equity: { background: "#faf5ff", color: "#6b46c1" },
    Revenue: { background: "#f0fff4", color: "#2f855a" },
    Expense: { background: "#fff5f5", color: "#c53030" },
  };
  return {
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    ...(styles[type] || { background: "#edf2f7", color: "#4a5568" }),
  };
};

export default COAList;
